import React, { useEffect, useState } from 'react';
import { CalendarHeart, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { staffAttApi, errMsg } from '@/services/staffAttendanceApi';

const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const wd = (d) => WD[(new Date(`${d}T00:00:00`).getDay() + 6) % 7];
const BLANK = { date: '', name: '', own: false, female: { start: '10:30', end: '16:30' }, male: { start: '09:30', end: '17:00' } };

const Times = ({ label, v, onChange, testId }) => (
  <div className="flex items-center gap-1.5 text-sm">
    <span className="w-14 text-gray-600">{label}</span>
    <Input type="time" value={v?.start || ''} onChange={(e) => onChange({ ...v, start: e.target.value })} className="h-8 w-[110px] bg-white" data-testid={`${testId}-start`} />
    <span className="text-gray-400">–</span>
    <Input type="time" value={v?.end || ''} onChange={(e) => onChange({ ...v, end: e.target.value })} className="h-8 w-[110px] bg-white" data-testid={`${testId}-end`} />
  </div>
);

const DayForm = ({ f, setF, busy, onSave, onCancel }) => (
  <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="special-day-form">
    <div className="flex flex-wrap items-center gap-2">
      <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="h-9 w-[160px] bg-white" data-testid="special-day-date" />
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name, e.g. Deepavali / Sunday working" className="h-9 flex-1 min-w-[200px] bg-white" data-testid="special-day-name" />
    </div>
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input type="checkbox" checked={f.own} onChange={(e) => setF({ ...f, own: e.target.checked })} data-testid="special-day-own-timing" />Different timing for this day
    </label>
    {f.own && <div className="space-y-1.5 pl-5">
      <Times label="Women" v={f.female} onChange={(v) => setF({ ...f, female: v })} testId="special-day-female" />
      <Times label="Men" v={f.male} onChange={(v) => setF({ ...f, male: v })} testId="special-day-male" />
    </div>}
    <div className="flex gap-2">
      <Button size="sm" onClick={onSave} disabled={busy} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="special-day-save-btn">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save Special Day</Button>
      <Button size="sm" variant="ghost" onClick={onCancel} data-testid="special-day-cancel-btn">Cancel</Button>
    </div>
  </div>
);

const DayRow = ({ d, defaults, onEdit, onRemove }) => {
  const t = (k) => d[k] || defaults[k];
  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm" data-testid={`special-day-row-${d.date}`}>
      <span className="font-mono font-semibold">{d.date}</span><span className="text-xs text-gray-500">{wd(d.date)}</span>
      <span className="font-medium">{d.name}</span>
      <span className="text-xs text-gray-600" data-testid={`special-day-timing-${d.date}`}>Women {t('female').start}–{t('female').end} · Men {t('male').start}–{t('male').end}{d.female || d.male ? '' : ' (default)'}</span>
      <div className="ml-auto flex gap-1">
        <Button size="sm" variant="ghost" className="h-8" onClick={() => onEdit(d)} data-testid={`special-day-edit-${d.date}`}><Pencil className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" className="h-8 text-rose-600" onClick={() => onRemove(d)} data-testid={`special-day-remove-${d.date}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </li>
  );
};

/** HR: Special Day timing (weekly offs use it on their own) + festival days marked with the default or their own timing. */
export const SpecialDays = ({ s, set }) => {
  const [days, setDays] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const defaults = { female: s.special_female, male: s.special_male };
  const load = () => staffAttApi.specialDays().then((r) => setDays(r.days)).catch((e) => { setDays([]); toast.error(errMsg(e)); });
  useEffect(() => { load(); }, []);
  const save = () => {
    if (!f.date || !f.name.trim()) { toast.error('Pick the date and give the Special Day a name'); return; }
    setBusy(true);
    staffAttApi.markSpecialDay(f.date, { name: f.name, female: f.own ? f.female : null, male: f.own ? f.male : null })
      .then(() => { toast.success(`${f.date} marked as a Special Day — Late / Left early use its timing`); setF(null); load(); })
      .catch((e) => toast.error(errMsg(e, 'Could not save'))).finally(() => setBusy(false));
  };
  const remove = (d) => {
    if (!window.confirm(`Remove Special Day ${d.date} (${d.name})? The normal shift timing applies again.`)) return;
    staffAttApi.unmarkSpecialDay(d.date).then(() => { toast.success(`${d.date} is a normal day again`); load(); }).catch((e) => toast.error(errMsg(e)));
  };
  const edit = (d) => setF({ date: d.date, name: d.name, own: !!(d.female || d.male), female: d.female || defaults.female, male: d.male || defaults.male });
  return (
    <Card className="border-0 shadow-md" data-testid="special-days-settings"><CardContent className="p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="max-w-xl">
          <h3 className="font-semibold flex items-center gap-2"><CalendarHeart className="w-4 h-4 text-amber-600" />Special Days</h3>
          <p className="text-sm text-gray-500">Sundays and festival days run on a shorter timing. <b>Weekly offs use it on their own</b>; mark festival days below. Late / Left early use this timing for every section — past days are corrected too.</p>
        </div>
        {!f && <Button size="sm" variant="outline" onClick={() => setF({ ...BLANK, female: defaults.female, male: defaults.male })} data-testid="special-day-add-btn"><Plus className="w-4 h-4 mr-1" />Mark a Special Day</Button>}
      </div>
      <div className="space-y-1.5 rounded-lg bg-slate-50 p-3" data-testid="special-default-timing">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Special Day timing (saved with Save Settings)</div>
        <Times label="Women" v={s.special_female} onChange={(v) => set({ special_female: v })} testId="special-default-female" />
        <Times label="Men" v={s.special_male} onChange={(v) => set({ special_male: v })} testId="special-default-male" />
      </div>
      {f && <DayForm f={f} setF={setF} busy={busy} onSave={save} onCancel={() => setF(null)} />}
      <ul className="max-h-72 divide-y overflow-y-auto rounded-lg border border-gray-200 bg-white" data-testid="special-days-list">
        {days === null ? <Loader2 className="w-5 h-5 mx-auto my-4 animate-spin text-amber-600" />
          : days.length === 0 ? <li className="py-6 text-center text-sm text-gray-500" data-testid="special-days-empty">No festival days marked yet</li>
            : days.map((d) => <DayRow key={d.date} d={d} defaults={defaults} onEdit={edit} onRemove={remove} />)}
      </ul>
    </CardContent></Card>
  );
};

export default SpecialDays;
